import { Component, Inject, Input, OnDestroy, OnInit, Optional } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CONFIG_MODAL_INPUTS_TOKEN, ModalOverlayRef } from '@core/classes/modal-overlay.ref';
import { Currency, DataTypeOptions, getCurrencyKey, University } from '@core/constants/data-types.constants';
import { UniversitiesByLocation } from '@core/models/config.model';
import { CurrencyAmount, DummyCurrencyAmount } from '@core/models/currency-amount.model';
import { convert, CurrencyExchange } from '@core/models/currency-exchange';
import { LifePlan } from '@core/models/life-plan.model';
import { adjustValuesToCurrency } from '@core/models/product.model';
import { LifePlanManagerService } from '@core/services/life-plan-manager.service';
import { ReportsService } from '@core/services/reports.service';
import { UiService } from '@core/services/ui.service';
import { Store } from '@ngrx/store';
import * as actions from '@redux/actions';
import { AppState } from '@redux/app.reducers';
import { getFieldError, isFieldInvalid, isFieldValid } from '@shared/utils/forms/validations.util';
import { Subscription } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

@Component({
  selector: 'mfp-university',
  templateUrl: './university.component.html',
  styleUrls: ['./university.component.scss']
})
export class UniversityComponent implements OnInit, OnDestroy {
  @Input() insuranceTargetId = '';
  universityCost = DummyCurrencyAmount;

  universityForm: FormGroup;

  isFieldValid = isFieldValid;
  isFieldInvalid = isFieldInvalid;
  getFieldError = getFieldError;
  getCurrencyKey = getCurrencyKey;

  $storeSubscription: Subscription;

  locations: DataTypeOptions<string> = [];
  universities: DataTypeOptions<University> = [];
  universitiesByLocation: UniversitiesByLocation = {};
  currencyOptions: DataTypeOptions<Currency> = [];
  currencyExchange: CurrencyExchange = {};
  currency: Currency = '';

  lifePlan!: LifePlan;

  constructor(
    private formBuilder: FormBuilder,
    public dialogRef: ModalOverlayRef,
    private store: Store<AppState>,
    private reportsService: ReportsService,
    private lifePlanManager: LifePlanManagerService,
    private uiService: UiService,
    @Optional() @Inject(CONFIG_MODAL_INPUTS_TOKEN) config: any
  ) {
    if (config) {
      this.insuranceTargetId = config.insuranceTargetId;
    }
    this.universityForm = this.formBuilder.group({
      location: ['', [Validators.required]],
      university: [{ value: '', disabled: true }, [Validators.required]]
    });
    this.$storeSubscription = this.store.subscribe({
      next: state => {
        this.currencyOptions = state.config.currencies;
        this.currencyExchange = state.config.currencyExchange;
        this.universitiesByLocation = state.config.universitiesByLocation;
        this.locations = [];
        for (const key in this.universitiesByLocation) {
          if (Object.prototype.hasOwnProperty.call(this.universitiesByLocation, key)) {
            this.locations.push({
              key,
              value: key
            });
          }
        }
        this.lifePlan = state.lifePlan.currentLifePlan;
        this.currency = state.lifePlan.currentLifePlan.insurances![
          this.insuranceTargetId
        ].currentProduct!.selectedCurrency;
      }
    });
    this.setUniversityForm();
  }

  ngOnInit(): void {
    this.universityForm.controls.location.valueChanges.subscribe({
      next: value => {
        if (value) {
          this.universityForm.controls.university.enable();
          this.universityForm.controls.university.setValue('');
          this.universityCost = DummyCurrencyAmount;
          this.setUnivesities(value);
        }
      }
    });
    this.universityForm.controls.university.valueChanges.subscribe({
      next: value => {
        if (value) {
          this.setCosts(this.universityForm.value.location, value);
        }
      }
    });
  }

  ngOnDestroy(): void {
    this.$storeSubscription.unsubscribe();
  }

  close() {
    this.dialogRef.close();
  }

  save() {
    this.universityForm.markAllAsTouched();
    if (this.universityForm.valid) {
      this.uiService.presentLoading();
      this.store.dispatch(
        actions.updateInsuranceTargetUniversity({
          insuranceTargetId: this.insuranceTargetId,
          university: this.universityForm.value.university,
          universityCost: this.universityCost
        })
      );
      this.lifePlanManager
        .generateLifePlanInsuranceTargetsAndPopulateThemWithProducts(this.lifePlan, this.insuranceTargetId)
        .pipe(
          switchMap(lp =>
            this.reportsService.getAndUpdateReportsData(lp).pipe(
              map(() => {
                const product = lp.insurances![this.insuranceTargetId].currentProduct!;
                adjustValuesToCurrency(product, this.currency, this.currencyExchange);
                return lp;
              }),
              switchMap(() => this.lifePlanManager.updateAndManageLifePlan(lp))
            )
          )
        )
        .subscribe({
          error: error => {
            this.uiService.dismissLoading();
            this.uiService.openErrorModal();
          },
          complete: () => {
            this.uiService.dismissLoading();
            this.dialogRef.close();
          }
        });
    }
  }

  private setUniversityForm() {
    if (this.lifePlan.insurances) {
      const insuredData = this.lifePlan.insurances[this.insuranceTargetId].insured;
      if (insuredData.university && insuredData.universityCost) {
        let univesityLocation = '';
        for (const location in this.universitiesByLocation) {
          if (Object.prototype.hasOwnProperty.call(this.universitiesByLocation, location)) {
            const universities = this.universitiesByLocation[location];
            const university = universities.find(u => u.option.value === insuredData.university);
            if (university) {
              univesityLocation = location;
            }
          }
        }

        this.universityForm.setValue({
          location: univesityLocation,
          university: insuredData.university
        });
        if (univesityLocation) {
          this.universityForm.controls.university.enable();
          this.setUnivesities(univesityLocation);
        }
        this.universityCost = convert(insuredData.universityCost, this.currency, this.currencyExchange);
      }
    }
  }

  setUnivesities(location: string) {
    this.universities = this.universitiesByLocation[location]?.map(u => u.option);
  }

  setCosts(location: string, university: string) {
    const costs = this.universitiesByLocation[location]?.find(u => u.option.value === university)?.params;
    if (costs) {
      this.universityCost = convert(costs.universityCost, this.currency, this.currencyExchange);
    }
  }
}
